import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Trash2, AtSign } from "lucide-react";

const formatRel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return d.toLocaleDateString();
};

// Renders @mentions as styled spans
function renderBody(body) {
  const parts = body.split(/(@[A-Za-z0-9._-]+)/g);
  return parts.map((p, i) =>
    p.startsWith("@") ? <span key={i} className="text-amber-700 font-medium">{p}</span> : <span key={i}>{p}</span>
  );
}

export default function CommentsThread({ targetType, targetId, currentUserId, role }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const canComment = role && role !== "viewer";

  const load = async () => {
    try {
      const { data } = await api.get("/comments", { params: { target_type: targetType, target_id: targetId } });
      setComments(data);
    } catch (e) {
      // silent — caller may not have access
    }
  };

  useEffect(() => { load(); }, [targetType, targetId]);

  const post = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/comments", { target_type: targetType, target_id: targetId, body });
      setComments((p) => [...p, data]);
      setBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments((p) => p.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-stone-100" data-testid={`comments-thread-${targetType}-${targetId}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">
        <MessageCircle className="w-3.5 h-3.5" />
        Comments {comments.length > 0 && `· ${comments.length}`}
      </div>

      <ul className="space-y-3">
        {comments.map((c) => (
          <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            {c.author_avatar ? (
              <img src={c.author_avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 border border-stone-200" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 grid place-items-center text-xs font-semibold shrink-0">
                {(c.author_name || "?")[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-sm text-stone-900 truncate">{c.author_name}</span>
                <span className="text-xs text-stone-400">{formatRel(c.created_at)}</span>
                {(c.author_id === currentUserId) && (
                  <button onClick={() => remove(c.id)} className="text-xs text-stone-400 hover:text-red-600 ml-auto" data-testid={`comment-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap break-words">{renderBody(c.body)}</div>
            </div>
          </li>
        ))}
      </ul>

      {canComment ? (
        <form onSubmit={post} className="mt-4 flex gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a comment… use @name to mention co-keepers"
            rows={2}
            maxLength={5000}
            className="text-sm"
            data-testid={`comment-input-${targetType}-${targetId}`}
          />
          <Button type="submit" disabled={posting || !body.trim()} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 self-end" data-testid={`comment-submit-${targetType}-${targetId}`}>
            {posting ? "…" : "Post"}
          </Button>
        </form>
      ) : (
        <div className="mt-3 text-xs text-stone-400 flex items-center gap-1">
          <AtSign className="w-3 h-3" /> Viewers cannot comment.
        </div>
      )}
    </div>
  );
}
